import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FileText, Ticket, Plane, Users, Hotel,
  BookOpen, BarChart3, Settings as SettingsIcon, LogOut, Menu, X,
} from "lucide-react";
import useBrand, { BrandMark } from "@/hooks/useBrand";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, tid: "nav-dashboard" },
  { to: "/invoices", icon: FileText, label: "Invoice", tid: "nav-invoices" },
  { to: "/etickets", icon: Ticket, label: "E-Ticket", tid: "nav-etickets" },
  { to: "/hotels", icon: Hotel, label: "Hotel", tid: "nav-hotels" },
  { to: "/customers", icon: Users, label: "Customers", tid: "nav-customers" },
  { to: "/accounting", icon: BookOpen, label: "Accounting", tid: "nav-accounting" },
  { to: "/reports", icon: BarChart3, label: "Reports", tid: "nav-reports" },
  { to: "/settings", icon: SettingsIcon, label: "Settings", tid: "nav-settings" },
];

export default function Layout() {
  const nav = useNavigate();
  const loc = useLocation();
  const brand = useBrand();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const logout = () => { localStorage.removeItem("gg_token"); nav("/login"); };

  // close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const Sidebar = ({ mobile = false }) => (
    <>
      <div className="p-5 border-b border-slate-100 flex items-center gap-3">
        <BrandMark brand={brand} size={40} />
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-slate-900 tracking-tight truncate">{brand.company_name || "Gogojalan"}</div>
          <div className="text-[11px] text-slate-500 truncate">{brand.tagline || "Go Easy · Go Smart"}</div>
        </div>
        {mobile && (
          <button onClick={() => setDrawerOpen(false)} className="p-2 -mr-2 rounded-lg hover:bg-slate-100" data-testid="drawer-close">
            <X size={20} className="text-slate-600" />
          </button>
        )}
      </div>

      <nav className="p-3 flex-1 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.tid}
            className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
          >
            <n.icon size={18} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <button
        onClick={logout}
        data-testid="logout-btn"
        className="m-3 flex items-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      >
        <LogOut size={16} /> <span className="text-sm">Logout</span>
      </button>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#f6f8f7]">
      {/* Desktop sidebar (>= md) */}
      <aside className="hidden md:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col">
        <Sidebar />
      </aside>

      {/* Mobile drawer + overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/40 transition-opacity ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setDrawerOpen(false)}
        data-testid="drawer-overlay"
        aria-hidden={!drawerOpen}
      />
      <aside
        className={`md:hidden fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-white z-50 flex flex-col transform transition-transform duration-200 shadow-xl ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-drawer"
      >
        <Sidebar mobile />
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center gap-3 px-3 py-2.5">
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-1 rounded-lg hover:bg-slate-100 active:bg-slate-200"
            data-testid="drawer-toggle"
            aria-label="Buka menu"
          >
            <Menu size={22} className="text-slate-700" />
          </button>
          <BrandMark brand={brand} size={32} />
          <div className="min-w-0 flex-1">
            <div className="font-extrabold text-slate-900 tracking-tight text-sm truncate">{brand.company_name || "Gogojalan"}</div>
          </div>
        </header>

        <div className="flex-1 overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 py-5 md:px-8 md:py-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
